import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import "./CommunityTables.css";

function CommunityTables() {
  const [uploadFile, setUploadFile] = useState(null);
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("");
  const [gameSearchQuery, setGameSearchQuery] = useState("");
  const [gameResults, setGameResults] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [loadingGames, setLoadingGames] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [agree, setAgree] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);

  const adRef = useRef(null);
  const processCloseBtnRef = useRef(null);
  const openProcessBtnRef = useRef(null);

  // Strict MAJOR.MINOR.PATCH, each part is a non-negative integer (no leading +/-, no letters)
  const VERSION_RE = /^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)$/;

  const handleVersionChange = (e) => {
    // Only allow digits and dots to be typed
    const v = e.target.value.trim();
    if (/^[0-9.]*$/.test(v)) setVersion(v);
  };


  // Lock body scroll when modal is open
  useEffect(() => {
    if (showProcessModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [showProcessModal]);
  
  // Esc to close modal
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === "Escape" && showProcessModal) setShowProcessModal(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showProcessModal]);

  // Focus management for modal
  useEffect(() => {
    if (showProcessModal && processCloseBtnRef.current) {
      processCloseBtnRef.current.focus();
    } else if (!showProcessModal && openProcessBtnRef.current) {
      openProcessBtnRef.current.focus();
    }
  }, [showProcessModal]);

  // AdSense (optional)
  useEffect(() => {
    if (!adRef.current) return;
    if (!adRef.current.hasAttribute("data-adsbygoogle-status")) {
      adRef.current.innerHTML = "";
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, []);

  // Debounced Steam search
  useEffect(() => {
    if (gameSearchQuery.trim().length < 2) {
      setGameResults([]);
      return;
    }
    const fetchGames = async () => {
      setLoadingGames(true);
      setError("");
      try {
        const res = await axios.get(
          `/api/steam/search?query=${encodeURIComponent(gameSearchQuery.trim())}`
        );
        setGameResults(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setGameResults([]);
        setError("Failed to search games. Please try again.");
      } finally {
        setLoadingGames(false);
      }
    };
    const t = setTimeout(fetchGames, 300);
    return () => clearTimeout(t);
  }, [gameSearchQuery]);

  const handleGameSelect = (game) => {
    setSelectedGame(game);
    setGameSearchQuery(game.name);
    setGameResults([]);
    setError("");
    setSuccess("");
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const desc = description.trim();
    const ver = version.trim();

    if (!selectedGame) {
      setError("Please select a game.");
      return;
    }
    if (!uploadFile) {
      setError("Please choose a .ct file to upload.");
      return;
    }
    if (!desc) {
      setError("Please add a description for your table.");
      return;
    }
    if (!ver) {
      setError("Please enter a table version (e.g., 1.0, 1.0.3).");
      return;
    }
    if (!VERSION_RE.test(ver)) {
      setError("Version must be MAJOR.MINOR.PATCH (e.g., 1.0.0).");
      return;
    }
    if (!agree) {
      setError("You must agree to the submission policy before uploading.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("appid", selectedGame.appid);
    formData.append("gameName", selectedGame.name);
    formData.append("description", desc);
    formData.append("version", ver);

    try {
      setSubmitting(true);
      const headers = localStorage.getItem("token")
        ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
        : {};
      await axios.post("/api/community/tables", formData, { headers });
      setSuccess("Thanks! Your table was submitted for verification. You’ll see it appear once it’s approved.");
      setUploadFile(null);
      setDescription("");
      setVersion("");
      setSelectedGame(null);
      setGameSearchQuery("");
      setAgree(false);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Upload failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="community-upload-page">
      <Helmet>
        <title>Submit a Community Cheat Table</title>
        <meta
          name="description"
          content="Upload your Cheat Engine table for review. We scan for malware and verify functionality before publishing."
        />
      </Helmet>

      <section className="hero">
        <div className="hero-inner">
          <h1>Submit a Community Cheat Table</h1>
          <p>
            Share your work with the community. We’ll review your submission for security and
            functionality before it goes live.
          </p>
          <div className="ad-container">
            <ins
              ref={adRef}
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-3980892685014254"
              data-ad-slot="5067767497"
              data-ad-format="auto"
              data-full-width-responsive="true"
            />
          </div>
        </div>
      </section>

      {/* Removed the policy-cards section to avoid duplication; info lives in the popup modal */}

      <section className="submit-card">
        <h2>Upload Your Table</h2>
        <p className="muted">
          Accepted format: <code>.ct</code>. Keep descriptions concise (what it changes, supported
          game version, and any hotkeys).
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form className="upload-grid" onSubmit={handleUpload}>
          <div className="form-field full">
            <label htmlFor="gameSearch">Game</label>
            <input
              id="gameSearch"
              type="text"
              placeholder="Search the Steam game name or paste an AppID"
              value={gameSearchQuery}
              onChange={(e) => setGameSearchQuery(e.target.value)}
              autoComplete="off"
              required
            />
            {loadingGames && <p className="tiny muted">Searching…</p>}
            {gameResults.length > 0 && (
              <div className="game-results">
                {gameResults.map((game) => (
                  <button
                    type="button"
                    key={game.appid}
                    className="game-result-item"
                    onClick={() => handleGameSelect(game)}
                    title="Select this game"
                  >
                    <img
                      src={game.image}
                      alt={game.name}
                      onError={(e) => (e.currentTarget.src = "default-placeholder.jpg")}
                    />
                    <span>{game.name}</span>
                    <span className="appid">AppID: {game.appid}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedGame && (
              <div className="selected-game">
                <img
                  src={selectedGame.image}
                  alt={selectedGame.name}
                  onError={(e) => (e.currentTarget.src = "default-placeholder.jpg")}
                />
                <div>
                  <strong>{selectedGame.name}</strong>
                  <div className="tiny muted">AppID: {selectedGame.appid}</div>
                </div>
                <button
                  type="button"
                  className="link danger"
                  onClick={() => {
                    setSelectedGame(null);
                    setGameSearchQuery("");
                  }}
                >
                  Change
                </button>
              </div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="version">Table Version <span className="tiny muted">(required)</span></label>
            <input
              id="version"
              type="text"
              placeholder="e.g., 1.0.0"
              value={version}
              onChange={handleVersionChange}
              required
              inputMode="numeric"
              pattern="\d+\.\d+\.\d+"
              title="Use MAJOR.MINOR.PATCH, e.g., 1.2.3"
            />
          </div>

          <div className="form-field full">
            <label htmlFor="description">Description <span className="tiny muted">(required)</span></label>
            <textarea
              id="description"
              placeholder="What does this table change? Which game version is it for? Any caveats or hotkeys?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              required
            />
            <div className="tiny muted">{description.length}/1000</div>
          </div>

          <div className="form-field">
            <label htmlFor="file">.ct File</label>
            <input
              id="file"
              type="file"
              accept=".ct"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              required
            />
          </div>

          <div className="form-field checkbox full">
            <label className="check-wrap">
              <input
                id="agree-check"
                type="checkbox"
                className="visually-hidden"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                required
              />
              <span aria-hidden="true" className="check"></span>
              <span className="check-text">
                I understand my upload will be scanned for malware and reviewed for functionality
                and policy compliance. I agree not to submit harmful or illegal content.
              </span>
            </label>
          </div>


          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit for Review"}
            </button>
            <button
              ref={openProcessBtnRef}
              type="button"
              className="btn-secondary"
              onClick={() => setShowProcessModal(true)}
              aria-haspopup="dialog"
              aria-expanded={showProcessModal ? "true" : "false"}
              aria-controls="review-process-modal"
            >
              Learn about the review process
            </button>
          </div>
        </form>

        <div className="foot-note muted tiny">
          Need help? Join our Discord or read the quick start guide in “Programs”.
        </div>
      </section>

      {/* Popup Modal */}
      {showProcessModal && (
        <div
          id="review-process-modal"
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reviewProcessTitle"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowProcessModal(false);
          }}
        >
          <div className="modal-card" role="document">
            <div className="modal-header">
              <h3 id="reviewProcessTitle">How Our Review Works</h3>
              <button
                ref={processCloseBtnRef}
                className="modal-close"
                onClick={() => setShowProcessModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <h4>1) Security Scan</h4>
              <p>
                We scan each file for malware, tampering, and suspicious patterns. Obfuscated or
                potentially harmful files are rejected immediately.
              </p>

              <h4>2) Functionality Check</h4>
              <p>
                We open the table to verify it loads and that key features behave as described. If
                something doesn’t work, we’ll request an updated upload.
              </p>

              <h4>3) Content & Policy</h4>
              <ul>
                <li>No harmful or illegal content.</li>
                <li>NSFW content must be properly marked and follow local laws.</li>
                <li>Respect third-party rights (no stolen or redistributed work without permission).</li>
              </ul>

              <h4>4) Versioning & Description</h4>
              <ul>
                <li><strong>Version</strong> should reflect your table release (e.g., 1.0.3).</li>
                <li><strong>Description</strong> should include supported game version, main features, and any hotkeys or caveats.</li>
              </ul>

              <h4>5) Turnaround & Notifications</h4>
              <p>
                Reviews are typically quick but can vary based on volume. You’ll be notified after
                approval or if we need changes.
              </p>

              <h4>6) Updates</h4>
              <p>
                To update a table, upload a new version with an incremented version number and a brief
                note explaining the changes.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowProcessModal(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunityTables;
